import React, { useEffect, useState } from 'react';
import { Loader } from 'lucide-react';
import {
  getEmailJob,
  getEmailJobItems,
  isJobActive,
  type EmailJobProgress,
  type EmailJobItem,
} from './emailApi';

function StatusChip({ status }: { status: string }) {
  const s = String(status || '').toLowerCase();
  const styles: Record<string, string> = {
    scheduled: 'bg-amber-50 text-amber-700 border-amber-200',
    processing: 'bg-blue-50 text-blue-700 border-blue-200',
    queued: 'bg-blue-50 text-blue-700 border-blue-200',
    sent: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    completed: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    'partially sent': 'bg-amber-50 text-amber-700 border-amber-200',
    failed: 'bg-rose-50 text-rose-700 border-rose-200',
    cancelled: 'bg-slate-100 text-slate-500 border-slate-200',
    draft: 'bg-slate-100 text-slate-500 border-slate-200',
    skipped: 'bg-slate-100 text-slate-500 border-slate-200',
  };
  const cls = styles[s] || 'bg-slate-100 text-slate-600 border-slate-200';
  return (
    <span className={`inline-flex items-center px-2 py-0.5 text-[10px] font-bold rounded-full border ${cls}`}>
      {status}
    </span>
  );
}

/**
 * Live progress card for a broadcast job (POST /api/email/broadcast).
 *
 * Polls GET /api/admin/bulk-jobs/:id (+ items) while the job is active and
 * renders processed/succeeded/failed counts plus per-recipient status.
 * Used by the batch campaign wizard's Send step and any other surface that
 * needs broadcast progress without duplicating the polling logic.
 */
export function BroadcastJobProgress({
  jobId,
  initialStatus,
  recipientCount,
  deduplicated,
  onSettled,
}: {
  jobId: string;
  initialStatus?: string;
  recipientCount?: number;
  deduplicated?: boolean;
  onSettled?: (progress: EmailJobProgress) => void;
}) {
  const [progress, setProgress] = useState<EmailJobProgress | null>(null);
  const [items, setItems] = useState<EmailJobItem[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!jobId) return;
    let cancelled = false;
    let timer: ReturnType<typeof setInterval> | null = null;
    const poll = async () => {
      setLoading(true);
      try {
        const p = await getEmailJob(jobId);
        if (cancelled) return;
        if (p) {
          setProgress(p);
          const it = await getEmailJobItems(jobId, { limit: 500 });
          if (!cancelled) setItems(it);
          if (!isJobActive(p.status)) {
            if (timer) clearInterval(timer);
            onSettled?.(p);
          }
        }
      } catch {
        /* keep polling */
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    poll();
    timer = setInterval(poll, 4000);
    return () => {
      cancelled = true;
      if (timer) clearInterval(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId]);

  const processed = progress
    ? progress.completedItems + progress.failedItems + progress.skippedItems + progress.cancelledItems
    : 0;
  const total = progress?.totalItems ?? recipientCount ?? 0;

  return (
    <div className="border border-slate-200 rounded-xl p-4 space-y-3 bg-slate-50/60">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <StatusChip status={progress?.status || initialStatus || 'queued'} />
          <span className="text-xs font-bold text-slate-700">
            {deduplicated ? 'Duplicate send suppressed — already queued' : 'Broadcast job'}
          </span>
          <span className="text-[11px] text-slate-500 font-mono">{jobId}</span>
        </div>
        {loading && <Loader className="h-3.5 w-3.5 animate-spin text-brand" />}
      </div>
      <div className="w-full bg-slate-200 rounded-full h-2.5 overflow-hidden">
        <div
          className="h-full bg-blue-600 rounded-full transition-all duration-500"
          style={{ width: `${Math.min(100, progress?.progressPercent || 0)}%` }}
        />
      </div>
      <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-600">
        <span>
          Processed <strong>{processed}</strong>/{total}
        </span>
        <span className="text-emerald-700">
          Succeeded <strong>{progress?.completedItems ?? 0}</strong>
        </span>
        {(progress?.failedItems ?? 0) > 0 && (
          <span className="text-rose-700">
            Failed <strong>{progress?.failedItems}</strong>
          </span>
        )}
        {((progress?.queuedItems ?? 0) + (progress?.processingItems ?? 0)) > 0 && (
          <span className="text-blue-700">
            Pending <strong>{(progress?.queuedItems ?? 0) + (progress?.processingItems ?? 0)}</strong>
          </span>
        )}
      </div>
      {items.length > 0 && (
        <div className="max-h-48 overflow-y-auto border border-slate-100 rounded-xl divide-y divide-slate-50 bg-white">
          {items.map((item) => (
            <div key={item.id} className="flex items-center justify-between gap-2 px-3 py-1.5 text-xs">
              <span className="truncate font-medium text-slate-700">{item.recipientEmail || item.id}</span>
              <span className="flex items-center gap-2 shrink-0">
                {item.errorCode === 'DOCUMENT_GENERATION_FAILED' ? (
                  <span className="text-[10px] font-bold text-rose-700 bg-rose-50 border border-rose-200 rounded-full px-1.5 py-0.5 shrink-0">
                    Document generation failed — not sent
                  </span>
                ) : item.errorCode === 'ATTACHMENT_BUDGET_EXCEEDED' ? (
                  <span className="text-[10px] font-bold text-rose-700 bg-rose-50 border border-rose-200 rounded-full px-1.5 py-0.5 shrink-0">
                    Attachments over the size limit — not sent
                  </span>
                ) : null}
                {item.error ? (
                  <span className="text-[10px] text-rose-600 truncate max-w-[200px]" title={item.error}>
                    {item.error}
                  </span>
                ) : null}
                <StatusChip status={item.status} />
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
